import React, { useEffect, useState, useRef } from "react";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/layout/Sidebar";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Trash2,
  AlertCircle,
  Globe2,
  ChevronDown,
  Home,
  FileText,
  BarChart2,
  X,
  TreePine,
  Bookmark,
  Info,
  MountainSnow,
} from "lucide-react";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import {
  getUserSavedRegions,
  unsaveRegionForUser,
  SavedRegionResponse,
  getUserSavedIcebergs,
  unsaveIcebergForUser,
  SavedIceberg,
  fetchEnvironmentalData,
  canSaveRegion,
} from "@/services/environmentalDataService";
import { BOOKMARK_LIMITS } from "@/utils/subscriptionUtils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";
import UserAvatar from "@/components/UserAvatar";
import { useAuthModal } from "../context/SupabaseAuthContext";
import DeforestationTab from "@/components/panels/DeforestationTab";
import LandLossTab from "@/components/panels/LandLossTab";
import DesertificationTab from "@/components/panels/DesertificationTab";
import SpeciesTab from "@/components/panels/SpeciesTab";
import EnrichedDataTab from "@/components/panels/EnrichedDataTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronRight, ChevronLeft } from "lucide-react";
import UserMenu from "../components/auth/UserMenu";
import { useUserSubscription } from "../utils/subscriptionUtils";
import TopBar from "@/components/layout/TopBar";
import IcebergPanel, {
  IcebergLocationData,
} from "@/components/panels/IcebergPanel";
import { supabase } from "@/integrations/supabase/client";

const MyRegions = () => {
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const { theme } = useTheme();
  const { toast } = useToast();
  const [savedRegions, setSavedRegions] = useState<SavedRegionResponse[]>([]);
  const [savedIcebergs, setSavedIcebergs] = useState<SavedIceberg[]>([]);
  const [icebergDetails, setIcebergDetails] = useState<{
    [id: string]: IcebergLocationData;
  }>({});
  const [expandedIceberg, setExpandedIceberg] = useState<string | null>(null);
  const [regionData, setRegionData] = useState<{ [key: string]: any }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);
  const { tier } = useUserSubscription();
  const { setShowLogin } = useAuthModal();
  const [bookmarkLimit, setBookmarkLimit] = useState<{
    allowed: boolean;
    remaining: number;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }

    const loadSavedRegions = async () => {
      try {
        setIsLoading(true);
        const [regions, icebergs] = await Promise.all([
          getUserSavedRegions(user.id),
          getUserSavedIcebergs(user.id),
        ]);
        setSavedRegions(regions);
        setSavedIcebergs(icebergs);

        // Load environmental data for saved regions only
        const environmentalData = {};
        for (const region of regions) {
          if (region.region_id.startsWith("iceberg:")) continue;
          const data = await fetchEnvironmentalData(region.region_id);
          environmentalData[region.region_id] = data;
        }
        setRegionData(environmentalData);

        // Check bookmark limits
        const limitInfo = await canSaveRegion(user);
        setBookmarkLimit(limitInfo);
      } catch (error) {
        console.error("Error loading saved regions:", error);
        toast({
          title: "Error",
          description: "Failed to load saved regions. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedRegions();
  }, [user, navigate, toast]);

  const handleDelete = async (regionId: string) => {
    try {
      await unsaveRegionForUser(user!.id, regionId);
      setSavedRegions((prev) => prev.filter((r) => r.region_id !== regionId));
      setRegionData((prev) => {
        const next = { ...prev };
        delete next[regionId];
        return next;
      });
      toast({
        title: "Region Removed",
        description: "Region has been removed from your saved list.",
      });
      if (user) setBookmarkLimit(await canSaveRegion(user));
    } catch (error) {
      console.error("Error removing region:", error);
      toast({
        title: "Error",
        description: "Failed to remove region. Please try again.",
        variant: "destructive",
      });
    }
  };

  const loadIcebergDetails = async (icebergId: string) => {
    if (icebergDetails[icebergId]) return;
    try {
      const { data, error } = await supabase
        .from("icebergs")
        .select("*")
        .eq("iceberg_id", icebergId)
        .single();
      if (error || !data) return;
      const raw = data as Record<string, unknown>;

      type TrackingEntry = {
        latitude: number;
        longitude: number;
        observation_date: string;
        length_nm?: number | null;
        width_nm?: number | null;
        area_sqkm?: number | null;
        area_sqmi?: number | null;
        area_sqnm?: number | null;
        est_mean_draft?: number | null;
        est_volume?: number | null;
        min_est_annual_mass_loss_Gt_per_yr?: number | null;
        melt_data_source?: string | null;
        tracking_uncertainty_percent?: number | null;
        min_avg_annual_thickness_melt_rate_m_per_yr?: number | null;
        min_avg_annual_lateral_retreat_m_per_yr?: number | null;
      };

      const history: TrackingEntry[] = Array.isArray(raw.tracking_history)
        ? (raw.tracking_history as TrackingEntry[])
        : [];

      const latest =
        history.length > 0
          ? history.reduce((a, b) =>
              a.observation_date > b.observation_date ? a : b,
            )
          : null;

      const normalized: IcebergLocationData = {
        iceberg_id: String(raw.iceberg_id),
        latitude: latest != null ? Number(latest.latitude) : 0,
        longitude: latest != null ? Number(latest.longitude) : 0,
        length_nm: latest?.length_nm != null ? Number(latest.length_nm) : null,
        width_nm: latest?.width_nm != null ? Number(latest.width_nm) : null,
        area_sqmi: latest?.area_sqmi != null ? Number(latest.area_sqmi) : null,
        area_sqnm: latest?.area_sqnm != null ? Number(latest.area_sqnm) : null,
        area_sqkm: latest?.area_sqkm != null ? Number(latest.area_sqkm) : null,
        last_update:
          latest?.observation_date != null
            ? String(latest.observation_date)
            : null,
        est_mean_draft:
          latest?.est_mean_draft != null ? Number(latest.est_mean_draft) : null,
        est_volume:
          latest?.est_volume != null ? Number(latest.est_volume) : null,
        melt_data_source:
          latest?.melt_data_source != null
            ? String(latest.melt_data_source)
            : raw.melt_data_source != null
              ? String(raw.melt_data_source)
              : null,
        primary_tracking_agency:
          raw.primary_tracking_agency != null
            ? String(raw.primary_tracking_agency)
            : null,
        tracking_uncertainty_percent:
          latest?.tracking_uncertainty_percent != null
            ? Number(latest.tracking_uncertainty_percent)
            : raw["tracking_uncertainty_%"] != null
              ? Number(raw["tracking_uncertainty_%"])
              : null,
        min_avg_annual_thickness_melt_rate_m_per_yr:
          latest?.min_avg_annual_thickness_melt_rate_m_per_yr != null
            ? Number(latest.min_avg_annual_thickness_melt_rate_m_per_yr)
            : raw["min_avg_annual_thickness_melt_rate_m/yr"] != null
              ? Number(raw["min_avg_annual_thickness_melt_rate_m/yr"])
              : null,
        max_avg_annual_thickness_melt_rate_m_per_yr:
          raw["max_avg_annual_thickness_melt_rate_m/yr"] != null
            ? Number(raw["max_avg_annual_thickness_melt_rate_m/yr"])
            : null,
        min_avg_annual_lateral_retreat_m_per_yr:
          latest?.min_avg_annual_lateral_retreat_m_per_yr != null
            ? Number(latest.min_avg_annual_lateral_retreat_m_per_yr)
            : raw["min_avg_annual_lateral_retreat_m/yr"] != null
              ? Number(raw["min_avg_annual_lateral_retreat_m/yr"])
              : null,
        max_avg_annual_lateral_retreat_m_per_yr:
          raw["max_avg_annual_lateral_retreat_m/yr"] != null
            ? Number(raw["max_avg_annual_lateral_retreat_m/yr"])
            : null,
        min_est_annual_mass_loss_Gt_per_yr:
          latest?.min_est_annual_mass_loss_Gt_per_yr != null
            ? Number(latest.min_est_annual_mass_loss_Gt_per_yr)
            : raw["min_est_annual_mass_loss_Gt/yr"] != null
              ? Number(raw["min_est_annual_mass_loss_Gt/yr"])
              : null,
        max_est_annual_mass_loss_Gt_per_yr:
          raw["max_est_annual_mass_loss_Gt/yr"] != null
            ? Number(raw["max_est_annual_mass_loss_Gt/yr"])
            : null,
        tracking_history: [...history].sort((a, b) =>
          a.observation_date.localeCompare(b.observation_date),
        ),
      };

      setIcebergDetails((prev) => ({ ...prev, [icebergId]: normalized }));
    } catch (err) {
      console.error("Error loading iceberg details:", err);
    }
  };

  const handleDeleteIceberg = async (icebergId: string) => {
    try {
      await unsaveIcebergForUser(user!.id, icebergId);
      setSavedIcebergs((prev) =>
        prev.filter((i) => i.iceberg_id !== icebergId),
      );
      toast({
        title: "Iceberg Removed",
        description: "Iceberg has been removed from your saved list.",
      });
      if (user) setBookmarkLimit(await canSaveRegion(user));
    } catch (error) {
      console.error("Error removing iceberg:", error);
      toast({
        title: "Error",
        description: "Failed to remove iceberg. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-b from-background to-background/80">
      {/* Collapsible Navbar */}
      <Sidebar />

      <div className="flex-1 flex flex-col">
        {/* Header */}
        <TopBar
          title="Bookmarks"
          icon={<Bookmark className="h-4 w-4 text-[#40e0d0]" />}
          showBackButton={false}
          showSubscriptionBadge={true}
        />

        {/* Main Content */}
        <main className="flex-1 container mx-auto px-4 sm:px-6 py-8">
          <div className="space-y-6">
            {/* Bookmark Counter */}
            <div className="flex items-center justify-between gap-4 mb-6">
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 border-muted-foreground/30 text-muted-foreground hover:bg-muted-foreground/10"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>

              {/* Total bookmarks indicator */}
              <div className="ml-4 text-sm text-muted-foreground">
                <span className="font-medium text-foreground mr-1">
                  Total Bookmarks:
                </span>
                <span className="font-medium text-[#40e0d0] text-xl">
                  {savedRegions.length + savedIcebergs.length}
                </span>
              </div>
            </div>
            {/** Bookmark Counter */}
            {bookmarkLimit && (
              <TooltipProvider>
                <div className="flex items-center gap-4 bg-[#f3f4f6] dark:bg-[#272727] px-4 py-2 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm ${
                        bookmarkLimit.remaining === 0
                          ? "text-red-500"
                          : bookmarkLimit.remaining <= 2
                            ? "text-amber-500"
                            : "text-green-500"
                      }`}
                    >
                      {tier === "professional" ? (
                        <>
                          <span className="font-medium">
                            {savedRegions.length}
                          </span>{" "}
                          of 10 regions saved
                        </>
                      ) : (
                        <>
                          <span className="font-medium">
                            {bookmarkLimit.remaining}
                          </span>{" "}
                          of 10 bookmark slots remaining
                        </>
                      )}
                    </span>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-sm">
                          Bookmarks are subject to your account’s usage limits
                          and do not refresh..
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </TooltipProvider>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : savedRegions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Saved Regions</h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  You haven't saved any regions yet. Explore the map and save
                  regions to access them quickly here.
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate("/")}
                  className="mt-6"
                >
                  Explore Regions
                </Button>
              </div>
            ) : (
              <Accordion
                type="single"
                collapsible
                value={expandedRegion || undefined}
                onValueChange={(value) => setExpandedRegion(value)}
              >
                {savedRegions.map((savedRegion) => (
                  <AccordionItem
                    key={savedRegion.id}
                    value={savedRegion.region_id}
                  >
                    <AccordionTrigger className="hover:no-underline [&[data-state=open]>div>.view-details]:hidden">
                      <div className="flex items-center justify-between w-full pr-4 text-left">
                        <div className="flex-1">
                          {savedRegion.region ? (
                            <>
                              <h3 className="text-m font-semibold">
                                {savedRegion.region.name}
                              </h3>
                              <p className="text-xs text-muted-foreground">
                                {savedRegion.region.country} •{" "}
                                {savedRegion.region.continent}
                              </p>
                              <div className="flex mt-2 items-center gap-2 text-xs text-muted-foreground !text-[#40e0d0]">
                                <Globe2 className="h-4 w-4" />
                                <span className="hidden sm:inline">
                                  {savedRegion.region.coordinates[1].toFixed(2)}
                                  °N,{" "}
                                  {savedRegion.region.coordinates[0].toFixed(2)}
                                  °E
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <h3 className="text-m font-semibold flex items-center gap-2">
                                <MountainSnow className="h-4 w-4 text-sky-400" />
                                Iceberg{" "}
                                {savedRegion.region_id.replace(/^iceberg:/, "")}
                              </h3>
                              <p className="text-xs text-muted-foreground">
                                Antarctic Iceberg · Tracked by USNIC
                              </p>
                            </>
                          )}
                        </div>
                        {/* Delete region Button */}
                        <div className="flex items-center mr-6 pr-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(savedRegion.region_id);
                            }}
                            className="text-destructive hover:text-destructive text-[.6rem]"
                          >
                            <Trash2 className="h-1 w-1 mr-1" />
                            Remove Region
                          </Button>
                        </div>
                        <div className="flex items-center gap-4">
                          <Button
                            variant="outline"
                            size="sm"
                            className="view-details border-[#40e0d0] text-[#40e0d0] bg-[#40e0d0]/10 hover:bg-[#40e0d0]/20"
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pt-4">
                        {savedRegion.region_id.startsWith("iceberg:") ? (
                          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center text-muted-foreground">
                            <MountainSnow className="h-10 w-10 text-sky-400/60" />
                            <p className="text-sm">
                              Open the globe to view full tracking details for
                              iceberg{" "}
                              <span className="font-semibold text-foreground">
                                {savedRegion.region_id.replace(/^iceberg:/, "")}
                              </span>
                              .
                            </p>
                          </div>
                        ) : regionData[savedRegion.region_id] ? (
                          <Tabs defaultValue="deforestation" className="w-full">
                            <TabsList className="grid grid-cols-4 sm:grid-cols-5 mb-4">
                              <TabsTrigger value="deforestation">
                                Deforestation
                              </TabsTrigger>
                              <TabsTrigger value="landloss">
                                Land Loss
                              </TabsTrigger>
                              <TabsTrigger value="desertification">
                                Desertification
                              </TabsTrigger>
                              <TabsTrigger value="species">Species</TabsTrigger>
                              <TabsTrigger
                                value="enriched"
                                className="hidden sm:block"
                              >
                                Enriched
                              </TabsTrigger>
                            </TabsList>

                            <TabsContent value="deforestation">
                              <DeforestationTab
                                data={regionData[savedRegion.region_id]}
                              />
                            </TabsContent>

                            <TabsContent value="landloss">
                              <LandLossTab
                                data={regionData[savedRegion.region_id]}
                              />
                            </TabsContent>

                            <TabsContent value="desertification">
                              <DesertificationTab
                                data={regionData[savedRegion.region_id]}
                              />
                            </TabsContent>

                            <TabsContent value="species">
                              <SpeciesTab
                                data={regionData[savedRegion.region_id]}
                              />
                            </TabsContent>

                            <TabsContent value="enriched">
                              <EnrichedDataTab
                                regionId={savedRegion.region_id}
                              />
                            </TabsContent>
                          </Tabs>
                        ) : (
                          <div className="flex items-center justify-center p-8">
                            <p className="text-muted-foreground">
                              Loading region data...
                            </p>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}

            {/* ── Saved Icebergs section ──────────────────────────────── */}
            {savedIcebergs.length > 0 && (
              <div className="mt-8">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                  <MountainSnow className="h-4 w-4 text-sky-400" />
                  Saved Icebergs
                </h2>
                <Accordion
                  type="single"
                  collapsible
                  value={expandedIceberg || undefined}
                  onValueChange={(value) => {
                    const next = value || null;
                    setExpandedIceberg(next);
                    if (next) loadIcebergDetails(next);
                  }}
                >
                  {savedIcebergs.map((iceberg) => (
                    <AccordionItem key={iceberg.id} value={iceberg.iceberg_id}>
                      <AccordionTrigger className="hover:no-underline [&[data-state=open]>div>.view-details]:hidden">
                        <div className="flex items-center justify-between w-full pr-4 text-left">
                          <div className="flex-1">
                            <h3 className="text-m font-semibold flex items-center gap-2">
                              <MountainSnow className="h-4 w-4 text-sky-400" />
                              Iceberg {iceberg.iceberg_id}
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              Antarctic Iceberg · Tracked by USNIC
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteIceberg(iceberg.iceberg_id);
                            }}
                            title="Remove iceberg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pt-2">
                          {icebergDetails[iceberg.iceberg_id] ? (
                            <IcebergPanel
                              iceberg={icebergDetails[iceberg.iceberg_id]}
                            />
                          ) : (
                            <div className="flex items-center justify-center p-8">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sky-400" />
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default MyRegions;
